/**
 * System Pulse helpers, copied from the design's psVals / pulseVals (Aithora ConsoleV3.dc.html
 * L18321-18683, L26912-28225): its duration and clock formats, its colour tables and its chip
 * styles, value for value. The page styles every element with the design's own CSS strings
 * through sx().
 */
import type { ApiClient } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';

/** Load one route with the session's client (like the Overview's useApi). */
export function usePulseApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], enabled = true): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], { enabled });
}

/** Unwrap an /api/pulse envelope. */
export const env = <T,>(p: Promise<{ success: boolean; data: T }>) => p.then((r) => r.data);

/** Minutes as the station writes them: 38s, 4m 00s, 5h 40m, 1d 7h (design dur()). */
export function dur(m: number): string {
  m = Math.max(0, m);
  if (m < 1) return Math.round(m * 60) + 's';
  if (m < 60) return Math.floor(m) + 'm ' + String(Math.floor((m % 1) * 60)).padStart(2, '0') + 's';
  if (m < 1440) return Math.floor(m / 60) + 'h ' + Math.floor(m % 60) + 'm';
  return Math.floor(m / 1440) + 'd ' + Math.floor((m % 1440) / 60) + 'h';
}
/** dur() without its seconds ("1h 12m", "26m"). */
export const durNoSec = (m: number) => dur(m).replace(/ \d+s$/, '');

/** A clock time, "2:12 PM" (design hm()). */
export const hm = (t: number) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Minutes between an ISO time and now. */
export const minsSince = (iso: string | null | undefined, now: number) => (iso ? (now - Date.parse(iso)) / 60000 : null);

/** An age in the station's short words: 38s ago, 4m ago, 2h ago, 2d ago. */
export function agoShort(iso: string | null | undefined, now: number): string {
  if (!iso) return '–';
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return s + 's ago';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 48) return h + 'h ago';
  return Math.floor(h / 24) + 'd ago';
}

/** "1m 40s" from seconds. */
export function secsWords(s: number): string {
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m ? m + 'm ' + r + 's' : r + 's';
}

// ── Colours (psLive C, psVals C, pulseVals SPEC) ─────────────────────────────

/** Safety-path and device tones (psLive C): fg, tint, label. */
export const PC = {
  ok: ['#1F6B52', 'rgba(47,138,111,.13)', 'OK'],
  down: ['#B3261E', 'rgba(179,38,30,.12)', 'BROKEN'],
  degraded: ['#8A5A00', 'rgba(184,134,11,.16)', 'DEGRADED'],
  unknown: ['#54606C', 'rgba(34,39,46,.08)', 'NOT PROVEN'],
  na: ['#6B7480', 'rgba(34,39,46,.05)', 'N/A'],
} as const;
export type PKey = keyof typeof PC;
export const pathChip = (k: PKey) =>
  'align-self:flex-start;display:inline-flex;align-items:center;height:22px;padding:0 5px;border-radius:3px;font-size:14px;font-weight:800;letter-spacing:.06em;color:' + PC[k][0] + ';background:' + PC[k][1] + ';';

/** Problem tones (psVals C). */
export const QC = {
  down: ['#B3261E', 'rgba(179,38,30,.12)', 'DOWN'],
  degraded: ['#8A5A00', 'rgba(184,134,11,.16)', 'DEGRADED'],
  unknown: ['#54606C', 'rgba(34,39,46,.09)', 'NOT PROVEN'],
  ok: ['#1F6B52', 'rgba(47,138,111,.13)', 'OK'],
} as const;
export type QKey = keyof typeof QC;
export const qChip = (k: QKey) =>
  'display:inline-flex;align-items:center;align-self:flex-start;height:22px;padding:0 6px;border-radius:4px;font-size:14px;font-weight:800;letter-spacing:.07em;white-space:nowrap;color:' + QC[k][0] + ';background:' + QC[k][1] + ';';

/** The setup tabs' status vocabulary (pulseVals SPEC). */
export const SPEC = {
  ok: ['#2F5C97', 'rgba(79,138,214,.13)', '#2F5C97'],
  degraded: ['#B3261E', 'rgba(179,38,30,.09)', '#D2665C'],
  down: ['#FCFAF4', '#B3261E', '#B3261E'],
  unknown: ['#54606C', 'rgba(34,39,46,.08)', '#54606C'],
  'n/a': ['#54606C', 'rgba(34,39,46,.04)', 'rgba(34,39,46,.28)'],
} as const;
export type SpecKey = keyof typeof SPEC;
/** pulseVals chipS(). */
export const chipS = (c: string, bg: string) =>
  'display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.06em;font-weight:700;white-space:nowrap;color:' + c + ';background:' + bg + ';';
/** pulseVals specChip(). */
export const specChip = (s: SpecKey) =>
  'display:inline-flex;align-items:center;height:19px;padding:0 8px;border-radius:5px;' +
  'font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.07em;font-weight:700;white-space:nowrap;' +
  'color:' + SPEC[s][0] + ';background:' + SPEC[s][1] + ';' +
  (s === 'degraded' ? 'box-shadow:inset 0 0 0 1px rgba(179,38,30,.4);' : '') +
  (s === 'down' ? 'background:#B3261E;color:#FCFAF4;' : '');

// ── Shared design strings ────────────────────────────────────────────────────

export const BTN_DARK = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:#22272E;color:#fff;';
export const BTN_LIGHT = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:#fff;color:#22272E;border:1px solid rgba(34,39,46,.2);';
export const BTN_BLUE_TEXT = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;box-sizing:border-box;background:#fff;color:#2F5C97;border:1px solid rgba(34,39,46,.2);';
export const CAPTION = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;font-weight:700;color:#54606C;';
export const CARD = 'box-sizing:border-box;border:1px solid rgba(34,39,46,.14);border-radius:12px;background:#FCFAF4;padding:10px 12px;min-width:0;';

/** A fresh idempotency key for a write the back end de-duplicates. */
export const idemKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : 'k-' + Date.now() + '-' + Math.random().toString(36).slice(2));

/** Design pre(): an anchor that acts like a button. */
export const pre = (e?: { preventDefault?: () => void; stopPropagation?: () => void }) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
};

/** True when the viewer asked for less motion (the design reads the same query). */
export function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
